import { Arrays, ByteArrayOutput, Option, panic, safeExecute, Terminator, UUID } from "@opendaw/lib-std";
import { BoxEditing, BoxGraph } from "@opendaw/lib-box";
import { AudioBusBox, AudioUnitBox, BoxIO, CompressorDeviceBox, GrooveShuffleBox, RootBox, TimelineBox, UserInterfaceBox } from "@opendaw/studio-boxes";
import { BoxAdapters, IconSymbol, ParameterFieldAdapters, ProjectDecoder, RootBoxAdapter, TimelineBoxAdapter, UnionBoxTypes, UserEditingManager, VertexSelection } from "@opendaw/studio-adapters";
import { LiveStreamReceiver } from "@opendaw/lib-fusion";
import { AudioUnitType } from "@opendaw/studio-enums";
import { Mixer } from "../Mixer";
import { ProjectApi } from "./ProjectApi";
import { ProjectMigration } from "./ProjectMigration";
import { CaptureDevices, Recording } from "../capture";
import { EngineFacade } from "../EngineFacade";
import { MidiDevices, MIDILearning } from "../midi";
import { ProjectValidation } from "./ProjectValidation";
import { Preferences } from "../Preferences";
import { PPQN } from "@opendaw/lib-dsp";
// Main Entry Point for a Project
export class Project {
    static new(env, options) {
        const boxGraph = new BoxGraph(Option.wrap(BoxIO.create));
        const isoString = new Date().toISOString();
        console.debug(`New Project created on ${isoString}`);
        boxGraph.beginTransaction();
        const grooveShuffleBox = GrooveShuffleBox.create(boxGraph, UUID.generate(), box => {
            box.label.setValue("Groove Shuffle");
        });
        const rootBox = RootBox.create(boxGraph, UUID.generate(), box => {
            box.groove.refer(grooveShuffleBox);
            box.created.setValue(isoString);
        });
        const primaryAudioBus = AudioBusBox.create(boxGraph, UUID.generate(), box => {
            box.collection.refer(rootBox.audioBusses);
            box.label.setValue("Output");
            box.icon.setValue(IconSymbol.toName(IconSymbol.SpeakerHeadphone));
            box.color.setValue(/*Colors.blue*/ "hsl(189, 100%, 65%)"); // TODO
        });
        const primaryAudioOutputUnit = AudioUnitBox.create(boxGraph, UUID.generate(), box => {
            box.type.setValue(AudioUnitType.Output);
            box.collection.refer(rootBox.audioUnits);
            box.output.refer(rootBox.outputDevice);
            box.index.setValue(0);
        });
        if (Preferences.values["auto-create-output-compressor"]) {
            CompressorDeviceBox.create(boxGraph, UUID.generate(), box => {
                box.label.setValue("Compressor");
                box.index.setValue(0);
                box.host.refer(primaryAudioOutputUnit.audioEffects);
                box.threshold.setValue(0);
                box.ratio.setValue(24);
            });
        }
        const timelineBox = TimelineBox.create(boxGraph, UUID.generate());
        rootBox.timeline.refer(timelineBox.root);
        primaryAudioBus.output.refer(primaryAudioOutputUnit.input);
        const userInterfaceBoxes = [];
        const createDefaultUser = options?.noDefaultUser !== true;
        if (createDefaultUser) {
            const userInterfaceBox = UserInterfaceBox.create(boxGraph, UUID.generate());
            userInterfaceBox.root.refer(rootBox.users);
            userInterfaceBoxes.push(userInterfaceBox);
        }
        boxGraph.endTransaction();
        const project = new Project(env, boxGraph, {
            rootBox,
            primaryAudioBus,
            primaryAudioOutputUnit,
            userInterfaceBoxes,
            timelineBox
        });
        if (createDefaultUser) {
            project.follow(userInterfaceBoxes[0]);
        }
        return project;
    }
    static load(env, arrayBuffer) {
        return this.skeleton(env, ProjectDecoder.decode(arrayBuffer));
    }
    static skeleton(env, skeleton, followFirstUser = true) {
        ProjectMigration.migrate(skeleton);
        ProjectValidation.validate(skeleton);
        const project = new Project(env, skeleton.boxGraph, skeleton.mandatoryBoxes);
        if (followFirstUser) {
            project.follow(project.userInterfaceBoxes[0]);
        }
        return project;
    }
    #terminator = new Terminator();
    #env;
    boxGraph;
    rootBox;
    userInterfaceBoxes;
    masterBusBox;
    masterAudioUnit;
    timelineBox;
    api;
    captureDevices;
    editing;
    selection;
    boxAdapters;
    userEditingManager;
    parameterFieldAdapters;
    liveStreamReceiver;
    midiLearning;
    mixer;
    engine = new EngineFacade();
    constructor(env, boxGraph, { rootBox, userInterfaceBoxes, primaryAudioBus, primaryAudioOutputUnit, timelineBox }) {
        this.#env = env;
        this.boxGraph = boxGraph;
        this.rootBox = rootBox;
        this.userInterfaceBoxes = userInterfaceBoxes;
        this.masterBusBox = primaryAudioBus;
        this.masterAudioUnit = primaryAudioOutputUnit;
        this.timelineBox = timelineBox;
        this.api = new ProjectApi(this);
        this.editing = new BoxEditing(this.boxGraph);
        this.selection = new VertexSelection(this.editing, this.boxGraph);
        this.parameterFieldAdapters = new ParameterFieldAdapters();
        this.boxAdapters = this.#terminator.own(new BoxAdapters(this));
        this.userEditingManager = new UserEditingManager(this.editing);
        this.liveStreamReceiver = this.#terminator.own(new LiveStreamReceiver());
        this.midiLearning = this.#terminator.own(new MIDILearning(this));
        this.captureDevices = this.#terminator.own(new CaptureDevices(this));
        this.mixer = new Mixer(this.rootBoxAdapter.audioUnits);
        // TODO We are probably doing that from the outside
        if (this.userInterfaceBoxes.length === 1) {
            this.follow(this.userInterfaceBoxes[0]);
        }
        console.debug(`Project was created on ${this.rootBoxAdapter.created.toString()}`);
    }
    startAudioWorklet(restart, options) {
        console.debug(`start AudioWorklet`);
        const lifecycle = this.#terminator.spawn();
        const engine = lifecycle.own(this.#env.audioWorklets.createEngine({ project: this, options }));
        const handler = async (event) => {
            console.warn(event);
            // we will only accept the first error
            engine.removeEventListener("error", handler);
            engine.removeEventListener("processorerror", handler);
            lifecycle.terminate();
            await safeExecute(restart?.unload, event);
            safeExecute(restart?.load, this.startAudioWorklet(restart));
        };
        engine.addEventListener("error", handler);
        engine.addEventListener("processorerror", handler);
        engine.connect(engine.context.destination);
        this.engine.setWorklet(engine);
        return engine;
    }
    startRecording(countIn = true) {
        this.engine.assertWorklet();
        if (Recording.isRecording) {
            return;
        }
        Recording.start(this, countIn).finally();
    }
    follow(box) {
        this.userEditingManager.follow(box);
        this.selection.switch(box.selection);
    }
    own(terminable) { return this.#terminator.own(terminable); }
    ownAll(...terminables) { return this.#terminator.ownAll(...terminables); }
    spawn() { return this.#terminator.spawn(); }
    get env() { return this.#env; }
    get bpm() { return this.timelineBox.bpm.getValue(); }
    get rootBoxAdapter() { return this.boxAdapters.adapterFor(this.rootBox, RootBoxAdapter); }
    get timelineBoxAdapter() { return this.boxAdapters.adapterFor(this.timelineBox, TimelineBoxAdapter); }
    get sampleManager() { return this.#env.sampleManager; }
    get soundfontManager() { return this.#env.soundfontManager; }
    get clipSequencing() { return panic("Only available in audio context"); }
    get isAudioContext() { return false; }
    get isMainThread() { return true; }
    get liveStreamBroadcaster() { return panic("Only available in audio context"); }
    get signatureDuration() {
        const { nominator, denominator } = this.timelineBox.signature;
        return PPQN.fromSignature(nominator.getValue(), denominator.getValue());
    }
    get skeleton() {
        return {
            boxGraph: this.boxGraph,
            mandatoryBoxes: {
                rootBox: this.rootBox,
                timelineBox: this.timelineBox,
                primaryAudioBus: this.masterBusBox,
                primaryAudioOutputUnit: this.masterAudioUnit,
                userInterfaceBoxes: this.userInterfaceBoxes
            }
        };
    }
    receivedMIDIFromEngine(midiDeviceId, data, relativeTimeInMs) {
        MidiDevices.get().ifSome(midiAccess => midiAccess.outputs
            .get(midiDeviceId)?.send(data, performance.now() + relativeTimeInMs));
    }
    collectSampleUUIDs() {
        return this.boxGraph.boxes()
            .filter(box => box.accept({ visitAudioFileBox: (_box) => true }))
            .map(box => box.address.uuid);
    }
    toArrayBuffer() {
        const output = ByteArrayOutput.create();
        output.writeInt(ProjectDecoder.MAGIC_HEADER_OPEN);
        output.writeInt(ProjectDecoder.FORMAT_VERSION);
        const boxGraphChunk = this.boxGraph.toArrayBuffer();
        output.writeInt(boxGraphChunk.byteLength);
        output.writeBytes(new Int8Array(boxGraphChunk));
        return output.toArrayBuffer();
    }
    copy(env) {
        return Project.load({ ...this.#env, ...env }, this.toArrayBuffer());
    }
    invalid() {
        // TODO Optimise. Flag changes somewhere.
        return this.boxGraph.boxes().some(box => box.accept({
            visitTrackBox: (box) => {
                for (const [current, next] of Arrays.iterateAdjacent(box.regions.pointerHub.incoming()
                    .map(({ box }) => UnionBoxTypes.asRegionBox(box))
                    .sort(({ position: a }, { position: b }) => a.getValue() - b.getValue()))) {
                    if (current.position.getValue() + current.duration.getValue() > next.position.getValue()) {
                        return true;
                    }
                }
                return false;
            }
        }) ?? false);
    }
    terminate() {
        console.debug("Project terminated");
        this.#terminator.terminate();
    }
}
